import React, { useState } from "react";
import { NumericFormat } from "react-number-format";
import { useSelector } from "react-redux";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  CircularProgress,
} from "@mui/material";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../config/firebaseConfig";
import {
  getKinshasaDate,
  getKinshasaTimeHMS,
} from "../../utils/getKinshasaDateTime";

const MyMatrixForm = ({ setOpen, setLoading }) => {
  const initialFormState = {
    terminal: "SEP",
    produit: "SU",
    quantite: "",
    numBL: "",
    numBL2: "",
    cuve1: "",
    avant1: "",
    apres1: "",
    cuve2: "",
    avant2: "",
    apres2: "",
    debordement: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [localLoading, setLocalLoading] = useState(false);
  // Removed error and success state

  const myAuthStore = useSelector((state) => state.auth);

  const isFormValid = () => {
    if (
      !formData.terminal ||
      !formData.produit ||
      !formData.quantite ||
      !formData.numBL
    ) {
      return false;
    }

    if (formData.cuve1 && (!formData.avant1 || !formData.apres1)) {
      return false;
    }

    if (formData.cuve2 && (!formData.avant2 || !formData.apres2)) {
      return false;
    }

    if ((formData.avant1 || formData.apres1) && !formData.cuve1) {
      return false;
    }

    if ((formData.avant2 || formData.apres2) && !formData.cuve2) {
      return false;
    }

    return true;
  };

  const resetForm = () => {
    setFormData(initialFormState);
    // Removed error and success reset
  };

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFieldChange = (field) => (event) => {
    setField(field, event.target.value);
  };

  const handleNumericChange = (field) => (values) => {
    setField(field, values.value);
  };

  const handleSubmit = async () => {
    if (!myAuthStore?.userId) {
      return;
    }

    if (!isFormValid()) {
      return;
    }

    setLocalLoading(true);
    setLoading?.(true);

    const date = getKinshasaDate();
    const timeKey = getKinshasaTimeHMS();
    const fieldKey = `APPROV_${timeKey}`;

    const payload = {
      [fieldKey]: {
        term: formData.terminal,
        produit: formData.produit,
        qte: formData.quantite,
        numbl: formData.numBL,
        numbl2: formData.numBL2 || null,
        cuve1: formData.cuve1 || null,
        avant1: formData.avant1 || null,
        apres1: formData.apres1 || null,
        cuve2: formData.cuve2 || null,
        avant2: formData.avant2 || null,
        apres2: formData.apres2 || null,
        deb: formData.debordement || null,
        timestamp: new Date().toISOString(),
      },
    };

    try {
      const docRef = doc(db, "users", myAuthStore.userId, "data", date);
      await setDoc(docRef, { ...payload }, { merge: true });

      resetForm();

      setTimeout(() => {
        if (setOpen) setOpen(false);
        setLoading?.(false);
      }, 1500);
    } catch (e) {
      setLoading?.(false);
    } finally {
      setLocalLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Box sx={{ width: "100%", maxWidth: 620 }}>
        {/* Removed error and success alerts */}

        <Box
          sx={{
            p: 1,
            width: "100%",
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 2,
          }}
        >
          {/* First row: Terminal | Produit | Qte (m³) */}
          <FormControl fullWidth size="small" required sx={{ minWidth: 0 }}>
            <InputLabel>Terminal</InputLabel>
            <Select
              label="Terminal"
              value={formData.terminal}
              onChange={handleFieldChange("terminal")}
            >
              <MenuItem value="SEP">SEP</MenuItem>
              <MenuItem value="LP">LP</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" required sx={{ minWidth: 0 }}>
            <InputLabel>Produit</InputLabel>
            <Select
              label="Produit"
              value={formData.produit}
              onChange={handleFieldChange("produit")}
            >
              <MenuItem value="SU">SU</MenuItem>
              <MenuItem value="GO">GO</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" required sx={{ minWidth: 0 }}>
            <NumericFormat
              customInput={TextField}
              size="small"
              label="Qte (m³)"
              fullWidth
              required
              value={formData.quantite}
              onValueChange={handleNumericChange("quantite")}
              allowNegative={false}
              thousandSeparator=" "
              decimalSeparator=","
              decimalScale={2}
              fixedDecimalScale
              format="### ###,##"
              mask="_"
            />
          </FormControl>

          <NumericFormat
            customInput={TextField}
            size="small"
            label="Num. BL 1"
            fullWidth
            required
            value={formData.numBL}
            onValueChange={handleNumericChange("numBL")}
            allowNegative={false}
            thousandSeparator=" "
            decimalScale={0}
            format="### ### ###"
            mask="_"
            sx={{ gridColumn: "span 3" }}
          />

          {/* BL 2 (optionnel) */}
          <NumericFormat
            customInput={TextField}
            size="small"
            label="Num. BL 2"
            fullWidth
            value={formData.numBL2}
            onValueChange={handleNumericChange("numBL2")}
            allowNegative={false}
            thousandSeparator=" "
            decimalScale={0}
            format="### ### ###"
            mask="_"
            sx={{ gridColumn: "span 3" }}
          />


          {[
            { cuve: "cuve1", avant: "avant1", apres: "apres1", label: "1" },
            { cuve: "cuve2", avant: "avant2", apres: "apres2", label: "2" },
          ].map(({ cuve, avant, apres, label }) => (
            <React.Fragment key={cuve}>
              <FormControl fullWidth size="small">
                <InputLabel>Cuve {label}</InputLabel>
                <Select
                  label={`Cuve ${label}`}
                  value={formData[cuve]}
                  onChange={handleFieldChange(cuve)}
                >
                  <MenuItem value="C1">C1</MenuItem>
                  <MenuItem value="C2">C2</MenuItem>
                  <MenuItem value="C3">C3</MenuItem>
                  <MenuItem value="C4">C4</MenuItem>
                </Select>
              </FormControl>

              <NumericFormat
                customInput={TextField}
                size="small"
                label="Avant"
                fullWidth
                value={formData[avant]}
                onValueChange={handleNumericChange(avant)}
                allowNegative={false}
                thousandSeparator=" "
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                format="### ###,##"
                mask="_"
                disabled={!formData[cuve]}
              />

              <NumericFormat
                customInput={TextField}
                size="small"
                label="Après"
                fullWidth
                value={formData[apres]}
                onValueChange={handleNumericChange(apres)}
                allowNegative={false}
                thousandSeparator=" "
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                format="### ###,##"
                mask="_"
                disabled={!formData[cuve]}
              />
            </React.Fragment>
          ))}

          <NumericFormat
            customInput={TextField}
            size="small"
            label="Débordement (L)"
            fullWidth
            value={formData.debordement}
            onValueChange={handleNumericChange("debordement")}
            allowNegative={false}
            thousandSeparator=" "
            decimalSeparator=","
            decimalScale={2}
            fixedDecimalScale
            format="### ### ###,##"
            mask="_"
            sx={{ gridColumn: "span 3" }}
          />
        </Box>

        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={!isFormValid() || localLoading}
            startIcon={localLoading && <CircularProgress size={20} />}
          >
            {localLoading ? "ENVOI..." : "ENVOYER"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MyMatrixForm;
